import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Calendar,
  Save,
  RotateCcw,
  Search
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from 'react-toastify'
import SACMasterModal from '../../components/SACMasterModal/SACMasterModal'
import '../../components/SACMasterModal/SACMasterModal.css'
import autoIncrementService from '../../services/autoIncrementService'
import serviceMasterService from '../../services/serviceMasterService'

const ServiceMasterForm = () => {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const [formData, setFormData] = useState({
    serviceCode: 'SER/0004',
    serviceDescription: '',
    active: 'Yes',
    selectSAC: '',
    gstRate: '',
    igstRate: '',
    cgstRate: '',
    sgstRate: ''
  })
  const [showSACModal, setShowSACModal] = useState(false)
  const [isLoadingServiceCode, setIsLoadingServiceCode] = useState(true)

  // Fetch auto-increment number for SERVICE-MASTER
  const fetchServiceCode = async () => {
    try {
      setIsLoadingServiceCode(true)
      const response = await autoIncrementService.getCurrentValue('SERVICE-MASTER')
      
      console.log('Auto-increment response:', response)
      
      // Handle different response structures
      let sequence = null
      
      if (response.success && response.data) {
        sequence = response.data
      } else if (response.data) {
        // Sometimes the data is directly in response.data
        sequence = response.data
      } else if (response) {
        // Sometimes the response itself is the data
        sequence = response
      }
      
      console.log('Sequence data:', sequence)
      
      if (sequence) {
        console.log('Sequence data:', sequence)
        
        // Use the formattedValue directly from backend
        if (sequence.formattedValue) {
          console.log('Using backend formatted value:', sequence.formattedValue)
          setFormData(prev => ({
            ...prev,
            serviceCode: sequence.formattedValue
          }))
        } else {
          console.error('No formattedValue found in sequence:', sequence)
          // Set a default service code without showing error
          setFormData(prev => ({
            ...prev,
            serviceCode: 'SER0001'
          }))
        }
      } else {
        console.error('No sequence data found in response:', response)
        // Set a default service code without showing error
        setFormData(prev => ({
          ...prev,
          serviceCode: 'SER0001'
        }))
      }
    } catch (error) {
      console.error('Error fetching service code:', error)
      // Set a default service code without showing error
      setFormData(prev => ({
        ...prev,
        serviceCode: 'SER0001'
      }))
    } finally {
      setIsLoadingServiceCode(false)
    }
  }

  // Load service code on component mount
  useEffect(() => {
    fetchServiceCode()
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    try {
      console.log('Saving Service Master data:', formData)
      
      // Get the next value when actually saving
      const response = await autoIncrementService.getNextValue('SERVICE-MASTER')
      if (response && response.success && response.data && response.data.formattedValue) {
        // Update the service code with the incremented value
        const updatedFormData = {
          ...formData,
          serviceCode: response.data.formattedValue
        }
        console.log('Updated form data with new service code:', updatedFormData)
        
        // Call the service master API to save the data
        const saveResponse = await serviceMasterService.createService(updatedFormData)
        
        if (saveResponse && saveResponse.success) {
          toast.success('Service Master saved successfully!')
          navigate('/sales/service-master')
        } else {
          toast.error(saveResponse?.message || 'Failed to save Service Master')
        }
      } else {
        toast.error('Failed to generate service code')
      }
    } catch (error) {
      console.error('Error saving Service Master:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        navigate('/login')
      } else {
        toast.error('Failed to save Service Master')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      serviceCode: 'SER/0004',
      serviceDescription: '',
      active: 'Yes',
      selectSAC: '',
      gstRate: '',
      igstRate: '',
      cgstRate: '',
      sgstRate: ''
    })
  }

  const handleBack = () => {
    navigate('/sales/service-master')
  }

  const handleSACSearch = () => {
    // Check if user is authenticated
    if (!isAuthenticated || !user) {
      toast.error('Please log in to access SAC master data')
      return
    }
    
    // Check if token exists
    const token = localStorage.getItem('token')
    if (!token) {
      toast.error('Authentication token not found. Please log in again.')
      return
    }
    
    setShowSACModal(true)
  }

  const handleSACSelect = (selectedSAC) => {
    setFormData(prev => ({
      ...prev,
      selectSAC: selectedSAC.sacCode,
      gstRate: selectedSAC.gstRate?.toString() || '',
      igstRate: selectedSAC.igstRate?.toString() || '',
      cgstRate: selectedSAC.cgstRate?.toString() || '',
      sgstRate: selectedSAC.sgstRate?.toString() || ''
    }))
    setShowSACModal(false)
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Masters',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Service Master',
      path: '/sales/service-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Service Master</h1>
          <p>Manage service master data</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Service Master</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Service Details</h3></div>
          {/* Row 1: Service Code, Service Description, Active, Select SAC */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="serviceCode" className="form-label required">
                Service Code
              </label>
              <input
                type="text"
                id="serviceCode"
                name="serviceCode"
                value={isLoadingServiceCode ? 'Generating...' : formData.serviceCode}
                readOnly
                className="form-input"
                placeholder="Auto-generated service code"
                style={{ 
                  backgroundColor: '#f9fafb', 
                  color: 'var(--color-text-secondary, #5B6478)', 
                  cursor: 'not-allowed' 
                }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="serviceDescription" className="form-label required">
                Service Description
              </label>
              <input
                type="text"
                id="serviceDescription"
                name="serviceDescription"
                value={formData.serviceDescription}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter service description"
              />
            </div>

            <div className="form-group">
              <label htmlFor="active" className="form-label required">
                Active
              </label>
              <select
                id="active"
                name="active"
                value={formData.active}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="selectSAC" className="form-label required">
                Select SAC
              </label>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  id="selectSAC"
                  name="selectSAC"
                  value={formData.selectSAC}
                  readOnly
                  className="form-input search-input"
                  placeholder="Select SAC code from modal"
                  style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                />
                <button
                  type="button"
                  className="search-button"
                  title="Search SAC"
                  onClick={handleSACSearch}
                >
                  <Search size={16} />
                </button>
              </div>
            </div>
          </div>

          <div className="section-header"><h3>Tax Rates</h3></div>
          {/* Row 2: GST Rate, IGST Rate, CGST Rate, SGST Rate */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="gstRate" className="form-label">
                GST Rate
              </label>
              <input
                type="number"
                id="gstRate"
                name="gstRate"
                value={formData.gstRate}
                className="form-input"
                step="0.01"
                min="0"
                placeholder="0.00"
                readOnly
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="igstRate" className="form-label">
                IGST Rate
              </label>
              <input
                type="number"
                id="igstRate"
                name="igstRate"
                value={formData.igstRate}
                className="form-input"
                step="0.01"
                min="0"
                placeholder="0.00"
                readOnly
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="cgstRate" className="form-label">
                CGST Rate
              </label>
              <input
                type="number"
                id="cgstRate"
                name="cgstRate"
                value={formData.cgstRate}
                className="form-input"
                step="0.01"
                min="0"
                placeholder="0.00"
                readOnly
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="sgstRate" className="form-label">
                SGST Rate
              </label>
              <input
                type="number"
                id="sgstRate"
                name="sgstRate"
                value={formData.sgstRate}
                className="form-input"
                step="0.01"
                min="0"
                placeholder="0.00"
                readOnly
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </form>
      </div>

      {/* SAC Master Modal */}
      <SACMasterModal
        isOpen={showSACModal}
        onClose={() => setShowSACModal(false)}
        onSelectSAC={handleSACSelect}
      />
    </div>
  )
}

export default ServiceMasterForm